<template>
  <div :class="['w-full py-24 relative overflow-hidden', isDark ? 'bg-black text-white' : 'bg-gray-50 text-slate-900']">
    <!-- Glow background for dark mode -->
    <div v-if="isDark" class="absolute right-0 top-1/4 w-[400px] h-[400px] rounded-full bg-indigo-500/5 blur-[120px] pointer-events-none"></div>
    <div v-if="isDark" class="absolute left-0 bottom-1/4 w-[400px] h-[400px] rounded-full bg-indigo-500/5 blur-[120px] pointer-events-none"></div>

    <div class="max-w-7xl mx-auto px-6 relative z-10 flex flex-col items-center">
      <!-- Title Section -->
      <span :class="['text-xs px-3.5 py-1.5 rounded-full font-semibold tracking-wider mb-4 inline-block', isDark ? 'bg-[#4C4FDC]/10 border border-[#4C4FDC]/20 text-[#A69EFF]' : 'bg-indigo-50 border border-indigo-100 text-indigo-600']">
        PRICING PLANS
      </span>
      
      <h2 :class="['text-3xl lg:text-4xl font-extrabold mb-6 leading-tight text-center max-w-3xl', isDark ? 'text-white' : 'text-slate-900']">
        {{ pricingData.title }}
      </h2>
      
      <p :class="['text-sm lg:text-base text-center mb-4 max-w-2xl', isDark ? 'text-slate-400' : 'text-slate-500']">
        {{ pricingData.description }}
      </p>

      <a :href="pricingData.linkText.link" target="_blank"
        :class="['text-sm font-semibold flex items-center gap-1 transition-colors mt-2 mb-12', isDark ? 'text-[#A69EFF] hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800']">
        {{ pricingData.linkText.label }} <i class="fas fa-chevron-right text-xs"></i>
      </a>

      <!-- Pricing Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full items-stretch">
        <div v-for="item in pricingData.colsList" :key="item.header.title" 
          :class="[
            'rounded-[28px] border p-8 flex flex-col justify-between transition-all duration-300 h-full relative group',
            isDark 
              ? 'bg-slate-900/40 border-slate-800/80 hover:border-[#4C4FDC]/50 hover:shadow-[0_20px_50px_rgba(76,79,220,0.12)]' 
              : 'bg-white border-slate-200/60 shadow-sm hover:border-indigo-500/50 hover:shadow-[0_20px_40px_rgba(79,70,229,0.06)]'
          ]"
        >
          <!-- Special highlight for specific packages (e.g. source code version) -->
          <div v-if="item.header.title.includes('源码')" 
            class="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#4C4FDC] to-pink-500 text-white shadow-md z-10">
            推荐 / 主推
          </div>

          <div>
            <!-- Header -->
            <div class="mb-6 border-b pb-6" :class="isDark ? 'border-slate-800' : 'border-slate-100'">
              <h3 :class="['text-xl font-bold mb-3', isDark ? 'text-white' : 'text-slate-900']">
                {{ item.header.title }}
              </h3>
              <!-- Price / Sub-heading -->
              <div :class="['text-2xl font-extrabold tracking-tight min-h-[40px] flex items-center', isDark ? 'text-[#A69EFF]' : 'text-indigo-600']" v-html="item.header.price"></div>
            </div>

            <!-- Features list -->
            <ul class="space-y-4 mb-8">
              <li v-for="(data, index) in item.dataList" :key="index" class="flex items-start gap-3">
                <template v-if="data !== ''">
                  <!-- Custom SVG Check icon -->
                  <svg class="w-4 h-4 text-emerald-500 mt-1 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <span :class="['text-xs leading-relaxed', isDark ? 'text-slate-300' : 'text-slate-600']">
                    {{ data }}
                  </span>
                </template>
              </li>
            </ul>
          </div>

          <!-- CTA Button -->
          <a :href="item.header.buy.link" target="_blank"
            :style="{ backgroundColor: item.header.buy.bgColor, color: item.header.buy.textColor }"
            class="w-full py-3.5 rounded-xl text-xs font-bold text-center transition-all duration-300 hover:brightness-110 shadow-sm block select-none">
            {{ item.header.buy.label }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from '#imports'
import type { Pricing1Type } from '~/types/home/pricing'

defineProps<{
  pricingData: Pricing1Type
}>()

const route = useRoute()
const isDark = computed(() => route.path.includes('/x'))
</script>
